/**
 * =========================================================================================
 * Component - Main (Component - App)
 * =========================================================================================
 **/

import React, {lazy, Suspense, useEffect, useMemo, useState} from 'react'
import {checkCookie, createCookie, eraseCookie, getCookie} from 'ubs-framework/v2/common/cookie';
import {IS_DEV} from 'ubs-framework/v2/common/constant';
import {ALLOCATION, COLOR, ON_BOARDING} from './common/constant';
import {onLogout, sendData, socketConnect} from './store/actions';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useRouteMatch} from 'react-router-dom';
import * as Icon from 'ubs-icons';
import {useUAlert} from "ubs-framework";
import {ERROR} from "./common/string";
import {isInApp} from 'ubs-framework/src/common/function'
import useSocket from "./app-hrms/common/hooks/useSocket";
import UNewSurveyPopup from "ubs-framework/v2/UNewSurveyPopup";
import {detectDevice} from 'ubs-framework/v2/common/function';
import UToast from "ubs-framework/v2/UToast";
import GlobalLogs from "./app-hrms/common/global-logs";
import UErrorPage from "ubs-framework/v2/UErrorPages/UErrorPage";
import withLocation from './app-hrms/common/hocs/location-service';
import usePlan from './app-hrms/common/hooks/usePlan';
import {PLAN} from 'ubs-framework/v2/common/constant';

const AppHrms = lazy(() => import( "./app-hrms"));
const RunningTicketPopup = lazy(() => import( "./app-hrms/common/running-ticket-popup"));
const PushNotificationBar = lazy(() => import( "./common/components/push-notification"));
const AppHRMSPublic = lazy(() => import( "./app-hrms/public"));

const publicPath = '/sign';

const App = (props) => {
    const __plan = usePlan();
    let matchPath = useRouteMatch(publicPath)?.path;
    const DISPATCH = useDispatch();
    const history = useHistory();
    const alert = useUAlert();

    const _ID = useSelector(e => e?.common?._id);
    const _UAC = useSelector(e => !!e?.hrms?.uac);
    const _ONB = useSelector(e => e?.hrms?.validUser); // THIS IS ONLY FOR ONBOARDING USER
    const _SOCKET_ID = useSelector(e => e.common.socketId);
    const _ORG_ID = useSelector(e => e.common.orgId);
    const _RSM = useSelector(e => e.common.repeatedSurveyModal);
    const _IS_GEO = useSelector(e => __plan(PLAN?.geoTagging) && e.hrms?.loginMethod?.GEO_TAGGING);
    const _RMP = useSelector(e => __plan(PLAN?.routeMap) && e.hrms?.routeMapPolicy);
    const _AGF = useSelector(e => __plan(PLAN?.geoFencing) && e.hrms.attendanceGEOFencing || []);
    const _P_BAR = useSelector(e => e.hrms?.paymentBar);

    const _FU_IDL = useSelector(state => state.hrms?.findUserIdsList || []);
    const _FAL_IDL = useSelector(state => state.hrms?.findAccessLevelIdsList || []);
    const _USERS = useSelector(state => state.hrms?.userObj || {});
    const _ALO = useSelector(state => state.hrms?.accessLevelObj || {});
    const _CC = useSelector(e => e.hrms.commentCounter)
    const _ERROR = useSelector(e => e?.common?.error)
    const _L = useSelector(e => e?.common?.location);

    const [runningTimerInfo, setRunningTimerInfo] = useState(null);

    const {on, off, refresh} = useSocket(res => {
        switch (res.code) {
            case 200: {
                switch (res.event) {
                    case 'displayReferralDialog': {
                        DISPATCH({type: 'SET_INVITE_MODAL', payload: res?.data?.isDisplay});
                        break;
                    }
                    case 'logout': {
                        if (res?.data?.type === 'resetPassword') onLogout();
                        else if (res?.data?.type === 'changePassword') {
                            alert.show({
                                title: `Change Password`,
                                description: `Your Account's Password has been changed${res?.data?.isAdmin ? ' by admin.' : '.'}<br/>Please login again.`,
                                icon: <span className="u_icon u_red-bgicon u_m-auto"><Icon.Warning fill={COLOR.u_red} size={40}/></span>,
                                close: res?.data?.isAdmin ? 'Logout' : 'OK',
                                onClose: () => onLogout()
                            })
                        }
                        break;
                    }
                    case 'hrms|master|globalConversation|unreadMessageFlag': {
                        DISPATCH({type: 'SET_COMMENT_COUNTER', payload: res.data.results})
                        break;
                    }
                    case 'unReadCountMessage': {
                        const counter = _CC?.map(data => {
                            if (data.referenceType === res?.data?.referenceType) data = res.data
                            return data;
                        })
                        DISPATCH({type: 'SET_COMMENT_COUNTER', payload: counter})
                        break;
                    }
                    case 'hrms|timeSheet|getCurrentTiming': {
                        if (_IS_GEO || _RMP?.isRouteMapEnabled || _AGF?.length > 0) {
                            props?.getLocationPermission()
                        }
                        break;
                    }
                    default:
                        return true;
                }
                break;
            }
            case 401: {
                onLogout();
                break;
            }
            case 403: {
                switch (res.event) {
                    case 'hrms|request|masterData|read': {
                        if (res.error) DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message}});
                        break
                    }
                    case 'hrms|ipblocking|user|blocked':
                    case 'authenticate':
                    case 'login': {
                        if (res.data.type === 'Login Restriction') DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message, type: 'full'}});
                        else if (res.data.type === 'productAccessLocked') DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.type === 'productAccessLocked' ? ERROR.removeProductAccess : ERROR.allowProductAccess, type: 'full'}});
                        else DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message, type: 'full'}});
                        break;
                    }
                    case 'hrms|helpDesk|ticket|nextOrPrevious': {
                        alert.show({
                            title: 'Tickets Unavailable',
                            description: res?.data?.error ?? '',
                            icon: <Icon.HalfTicket fill={COLOR.u_red} size={40}/>,
                        })
                        break;
                    }
                    case 'hrms|helpDesk|ticket|details': {
                        alert.show({title: 'Something went wrong.', description: res?.data?.error || res?.data?.message})
                        const path = history?.location?.pathname?.split('/');
                        path.splice(-1, 1);
                        history.push(`${path.join('/')}`);
                        break;
                    }
                    case 'hrms|helpDesk|ticket|update':
                    case 'hrms|helpDesk|ticket|delete':
                        if (res?.data?.error?.error) {
                            const findUser = new Set();
                            if (res?.data?.error?.runningTimers?.length !== 0) {
                                res?.data?.error?.runningTimers?.map(e => findUser.add(e.user))
                            }
                            if (findUser.size !== 0) DISPATCH({
                                type: 'FIND_MASTER_DATA_LIST',
                                payload: {findUserIdsList: [...findUser]}
                            });
                            setRunningTimerInfo(res?.data?.error)
                        } else alert.show({title: 'Something went wrong.', description: res?.data?.error || res?.data?.message})
                        break;
                    default:
                        if (res?.data?.type !== 'MISSING_DATA') alert.show({title: 'Something went wrong.', description: res?.data?.error || res?.data?.message})

                        break;
                }
                break;
            }
            case 404: {
                if (res.event === 'logout' && res.message === 'Not Found') onLogout();
                else DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message, type: 'full'}});
                break;
            }
            case 406: {
                switch (res.event) {
                    case 'authenticate': {
                        DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message, type: 'full'}});
                        break;
                    }
                    default:
                        break;
                }
                break
            }
            case 400:
            case 409:
            case 412:
            case 418:
            case 500: {
                const isEventExcluded = [
                    "hrms|master|employee|disable",
                    "hrms|onDutyRequestPolicy|application|create",
                    "hrms|onDutyRequestPolicy|application|update",
                    "hrms|document|email-template|update",
                    "hrms|documents|v1|create",
                    "hrms|document|email-template|preview",
                    "hrms|documents|employee|create",
                    'hrms|request|onBoard|create',
                    "hrms|leaveEncashment|request|create",
                    "hrms|leaveEncashment|request|update",
                    "hrms|budget|head|count",
                ].includes(res?.event);

                const isTypeExcluded = [
                    "MISSING_DATA",
                    "RUNNING_PREVIOUS",
                    "EARLY_OUT",
                    "RUNNING_PMS_TIMER",
                    "LATE_IN",
                    "SHIFT_NOT_STARTED",
                    "MAX_BREAK",
                    "SELFIE_REQUIRED",
                ].includes(res?.data?.type);

                const isEventForSpecialCase = [
                    "hrms|leave|application|create",
                    "hrms|leave|application|update",
                    "hrms|master|resignation|create",
                    "hrms|master|subCompany|status|update",
                    "hrms|documents|v1|update",
                    "hrms|onboarding|employee",
                ].includes(res?.event);

                if (res?.event === 'authenticate' && res?.data?.isCompanyMFA && !res?.data?.isUserMFASetup) {
                    if (res?.error) DISPATCH({type: 'SET_ERROR', payload: {description: res?.data?.error || res?.data?.message, isRedirectAccount: true, type: 'full'}});
                }

                if (!res?.data?.isDependencies && !isEventExcluded && !(isTypeExcluded || isEventForSpecialCase)) {
                    alert.show({title: 'Something went wrong.', description: res?.data?.error || res?.data?.message})
                }


                break
            }
            default:
                break;
        }
    })

    useEffect(() => {
        const split = document.location.pathname?.split('/')?.[2]
        if (split === 'dashboard' && (_IS_GEO || _RMP?.isRouteMapEnabled || _AGF?.length > 0)) {
            props?.getLocationPermission()
        }
    }, [document.location.pathname, _IS_GEO, _RMP?.isRouteMapEnabled, _AGF?.length])

    useEffect(() => {
        if (isInApp() && !!window?.ReactNativeWebView) {
            const ubs_token = new URLSearchParams(window.location.search).get('ubs_token');
            if (ubs_token) createCookie('ubs_token', ubs_token)
        }
        if (matchPath !== publicPath) {
            if (process.env.NODE_ENV !== 'production') {
                const iframe = document.getElementsByTagName('iframe')[0]
                if (iframe?.classList?.length === 0) iframe.style.display = 'none !important'
            }

            createCookie('ubsHRMSModule', 'hrms')
            if (checkCookie('ubs_token')) {
                /** Clear URL and connect socket */
                eraseCookie('previousURL');
                socketConnect(DISPATCH, history, flag => {
                    if (flag) {
                        const orgId = window.location.pathname.split('/')[1];
                        let data = {token: getCookie('ubs_token')};
                        if (orgId.length === 10) data.orgId = orgId;
                        const isOnBoardingRoute = window.location.pathname.split('/')?.[2] === 'employee-onboarding' && window.location.pathname.split('/')?.[3] === 'employee-preview';
                        if (getCookie('ubs_token')) {
                            if (isOnBoardingRoute) sendData('onboardingAuth', data);
                            else sendData('authenticate', {...data, platform: detectDevice(true)});
                        }
                        refresh()
                    } else {
                        /**
                         * Client server socket connection fail so
                         * TODO: Refresh popup code Goes here
                         */
                    }
                });
            } else {
                /**
                 * Store current URL in cookie and redirect to account module
                 */
                if (isInApp() && !!window?.ReactNativeWebView) {
                    onLogout()
                } else {
                    if (window.location.pathname !== '/logout') createCookie('previousURL', window.location.href);
                    window.location.href = process.env.REACT_APP_ACCOUNT_MODULE_URL;
                }

            }
        }
        if (process.env.NODE_ENV !== 'production') {
            document.body.classList.add('development')
        }
    }, []);

    useEffect(() => {
        if (matchPath !== publicPath) {
            // for ipa/apk
            if (isInApp()) {
                let url = document.location.href;
                if (!url?.includes(_ORG_ID)) {
                    history.push(`/${_ORG_ID}/dashboard`)
                }
            }
        }
    }, [document.location.href])

    useEffect(() => {
        if (matchPath !== publicPath) {
            refresh()
            return () => off();
        }
    }, [_CC, _IS_GEO, _AGF, _RMP?.isRouteMapEnabled, window?.ReactNativeWebView, _L])

    useEffect(() => {
        if (matchPath !== publicPath) {
            // if (process.env.NODE_ENV !== 'production') return;
            const userIds = new Set();
            const accessLevelIds = new Set();

            // check user ids
            if (_FU_IDL?.length !== 0) {
                _FU_IDL.map(e => {
                    // If the user id exists and is not already in _USERS
                    if (e?._id && !_USERS?.[e?._id]) userIds.add(e?._id)
                    else if (!_USERS?.[e]) userIds.add(e)  // If the user id does not exist or is already in _USERS
                })
                DISPATCH({type: 'FIND_MASTER_DATA_LIST', payload: {findUserIdsList: []}})
                if (userIds.size) sendData('hrms|master|get', {userIds: [...userIds]})
            }

            // check access level ids
            if (_FAL_IDL?.length !== 0) {
                _FAL_IDL.map(e => {
                    if (e?._id && !_ALO?.[e?._id]) accessLevelIds.add(e?._id) // If the access level id exists and is not already in _USERS
                    else if (!_ALO?.[e]) accessLevelIds.add(e) // If the access level id does not exist or is already in _ALO
                })
                DISPATCH({type: 'FIND_MASTER_DATA_LIST', payload: {findAccessLevelIdsList: []}})
                if (accessLevelIds.size) sendData('hrms|master|get', {accessLevelIds: [...accessLevelIds]})
            }
        }
    }, [_FU_IDL, _FAL_IDL])

    const handle = {
        redirectSurvey: e => {
            history.push(`/${_ORG_ID}/inbox/survey-request/${_RSM?.id}`)
            DISPATCH({type: 'SET_REPEATED_SURVEY_MODAL', payload: {flag: false, id: ''}});
        },
        close: e => {
            DISPATCH({type: 'SET_REPEATED_SURVEY_MODAL', payload: {flag: false, id: ''}});
        },
    }

    const handleMessage = (event) => {
        /**
         * for show alert popup if mobile location is off
         * */
        
        if (isInApp()) {
            if (event.data.code === 'log') {
            } else if (event.data.code === 3 || event.data.code === 8) {
            } else {
                const dataFromWebView = event.data;
                if(event.data.mocked){
                    delete dataFromWebView.lat
                    delete dataFromWebView.lng
                }
                DISPATCH({type: 'UPDATE_LOCATION', payload: {...dataFromWebView, permission: 'granted', mocked: event.data.mocked}});
            }
        }
    };

    useEffect(() => {
        if (isInApp()) window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [])

    return (
        <React.Fragment>
            <Suspense fallback={<div className="u_preloader"><Icon.Preloader/></div>}>
                {((window.location.hostname !== 'hrms.superworks.com') || ALLOCATION.USER_ID.includes(_ID)) && <GlobalLogs/>}
                {!!_ERROR && <UErrorPage title={_ERROR?.title} description={_ERROR?.description} module="hrms" isRedirectAccount={_ERROR?.isRedirectAccount} onLogout={onLogout}/>}
                <div className={`u_h-full u_hrms ${_P_BAR?.isPaymentbar ? 'u_paymentbar' : ''}`}>
                    {matchPath === publicPath ?
                        <React.Fragment>
                            <AppHRMSPublic/>
                        </React.Fragment>
                        : <React.Fragment>
                            {(_UAC || _ONB) ? // _ONB is for valid onboarding user
                                <React.Fragment>
                                    {runningTimerInfo && <RunningTicketPopup data={runningTimerInfo} close={setRunningTimerInfo}/>}
                                    {!isInApp() && <PushNotificationBar/>}
                                    <AppHrms/>
                                </React.Fragment> :
                                <div className="u_preloader">
                                    <div className="u_m-auto u_t-center">
                                        <Icon.Preloader/>
                                        <p>{!_SOCKET_ID ? 'Connecting' : 'Retrieving'}...</p>
                                    </div>
                                </div>
                            }
                        </React.Fragment>
                    }
                    <UToast/>
                    {/** Do not remove below p tag :
                     Usage: IT is using in copy status report
                     */}
                    <p style={{fontSize: '0rem', height: 0}} id="u_copy-text"/>

                    {_RSM?.flag && <UNewSurveyPopup redirect={() => handle.redirectSurvey()} close={() => handle.close()}/>}
                </div>
            </Suspense>
        </React.Fragment>
    )
}

export default withLocation(App);
